import { useState } from "react";
import { Button } from "../../ui/button";
import { isSensitive, parseDotEnv } from "./utils";

interface BulkPasteModalProps {
  onImport: (vars: Record<string, string>) => void;
  onClose: () => void;
}

export function BulkPasteModal({ onImport, onClose }: BulkPasteModalProps) {
  const [raw, setRaw] = useState("");
  const { valid, skipped } = parseDotEnv(raw);
  const count = Object.keys(valid).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-background rounded-xl shadow-float dark:ring-1 dark:ring-border w-full max-w-lg p-6 space-y-4">
        <div>
          <h2 className="text-h5">Paste .env file</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Paste the contents of your .env file. Comments and blank lines are
            ignored. Existing keys will be overwritten.
          </p>
        </div>
        <textarea
          className="w-full h-44 font-mono text-xs rounded-lg p-3 bg-muted/30 resize-none focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder={"DATABASE_URL=postgres://...\nAPI_KEY=sk-...\n# comment"}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          autoFocus
        />
        {raw && (
          <div className="text-xs space-y-2">
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">{count}</span>{" "}
              variable{count !== 1 ? "s" : ""} found
              {skipped.length > 0 && (
                <span className="text-destructive ml-1">
                  · {skipped.length} line{skipped.length !== 1 ? "s" : ""} skipped
                  (invalid key)
                </span>
              )}
            </p>
            {count > 0 && (
              <div className="rounded-lg p-2.5 max-h-32 overflow-y-auto space-y-0.5 bg-muted/20">
                {Object.entries(valid).map(([k, v]) => (
                  <p key={k} className="font-mono truncate">
                    <span className="text-foreground">{k}</span>
                    <span className="text-muted-foreground"> = </span>
                    <span className="text-muted-foreground">
                      {isSensitive(k) ? "••••••" : v || <em>empty</em>}
                    </span>
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={count === 0}
            onClick={() => {
              onImport(valid);
              onClose();
            }}
          >
            Import{" "}
            {count > 0 ? `${count} variable${count !== 1 ? "s" : ""}` : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}
